import { useTranslation } from '@/hooks/use-translation';
import { languages, type Language } from '@/i18n';
import { ChevronDown, Globe } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export function LanguageSwitcher() {
    const { language, setLanguage } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const currentLang = languages.find((l) => l.code === language) || languages[0];

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (lang: Language) => {
        setLanguage(lang);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-[#3d5a6e] text-slate-600 dark:text-[#8a9a9e] text-sm font-medium hover:bg-slate-50 dark:hover:bg-[#1a2530] hover:border-slate-300 dark:hover:border-[#5a8a9a] transition-all duration-200"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <Globe className="w-4 h-4" />
                <span>{currentLang.nativeName}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 py-1 w-32 rounded-lg border border-slate-200 dark:border-[#3d5a6e] bg-white dark:bg-[#0a0f14] shadow-lg z-50">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            type="button"
                            onClick={() => handleSelect(lang.code)}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${lang.code === language
                                    ? 'text-[#3d6a7a] dark:text-[#7ab8c9] bg-slate-50 dark:bg-[#1a2530]'
                                    : 'text-slate-600 dark:text-[#8a9a9e] hover:bg-slate-50 dark:hover:bg-[#1a2530]'
                                }`}
                            role="option"
                            aria-selected={lang.code === language}
                        >
                            {lang.nativeName}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
